import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Tables } from "@/integrations/supabase/types";

export type DbListing = Tables<"listings">;

// Public listing type without sensitive location fields
export interface PublicListing {
  id: string;
  user_id: string;
  type: string;
  material_type: string;
  quality_tier: string;
  quantity: number;
  unit: string;
  price_per_unit: number;
  available: boolean;
  city: string;
  state: string;
  description: string;
  title: string;
  photos: string[];
  environmental_data: any;
  geotechnical_data: any;
  created_at: string;
  updated_at: string;
  // These are null for unauthenticated users
  address?: string;
  lat?: number | null;
  lng?: number | null;
  zip?: string;
}

interface UseListingsOptions {
  search?: string;
  materialFilter?: string;
  qualityFilter?: string;
  stateFilter?: string;
  typeFilter?: string;
}

export function useListings(options: UseListingsOptions = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["listings", options, !!user],
    queryFn: async () => {
      // Always use the public RPC. It excludes street address/zip and
      // returns only rounded (city-block) coordinates so the marketplace
      // map keeps working without exposing precise GPS to non-owners.
      {
        const { data, error } = await supabase.rpc("get_public_listings" as any);
        if (error) throw error;

        let results = (data ?? []) as PublicListing[];

        // Client-side filtering for public listings
        if (options.materialFilter && options.materialFilter !== "all") {
          results = results.filter((l) => l.material_type === options.materialFilter);
        }
        if (options.qualityFilter && options.qualityFilter !== "all") {
          results = results.filter((l) => l.quality_tier === options.qualityFilter);
        }
        if (options.stateFilter && options.stateFilter !== "all") {
          results = results.filter((l) => l.state === options.stateFilter);
        }
        if (options.typeFilter && options.typeFilter !== "all") {
          results = results.filter((l) => l.type === options.typeFilter);
        }
        if (options.search) {
          const s = options.search.toLowerCase();
          results = results.filter(
            (l) => l.title.toLowerCase().includes(s) || l.city.toLowerCase().includes(s)
          );
        }

        return results;
      }
    },
  });
}

export function useListing(id: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["listing", id, user?.id ?? null],
    queryFn: async () => {
      if (!id) return null;

      // Owner-first: returns full address via the owner RLS policy.
      if (user) {
        const { data: owned } = await supabase
          .from("listings")
          .select("*")
          .eq("id", id)
          .eq("user_id", user.id)
          .maybeSingle();
        if (owned) return owned as PublicListing;
      }

      // Public RPC: no street address / zip, rounded coordinates.
      const { data, error } = await supabase.rpc("get_public_listings" as any);
      if (error) throw error;
      const listing = (data ?? []).find((l: any) => l.id === id);
      return (listing as PublicListing) ?? null;
    },
    enabled: !!id,
  });
}
